// MultiRoomEditor — lets the operator stack multiple rooms under one
// guest in a single booking.  Shared between the Reservations modal and
// the CheckInOut walk-in form so the experience is identical in both.
//
// Props:
//   rooms             : array of selectable room records (already filtered)
//   roomTypes         : array of room_type records (for hourly/daily defaults)
//   value             : array of room-line objects (controlled state)
//   billingMode       : current booking-level billing mode (used to label the rate)
//   onChange          : (next) => void — fires whenever the array changes
//   money             : (n) => "GH₵ X.XX" helper
//   computeRoomTotal? : (line, room) => number — optional override
//
// Each line is shaped:
//   { room_id, adults, children, rate_per_night, rate_per_hour, rate_per_day }
//
// We keep this list intentionally close to what the server expects so
// `onChange` output can be passed straight into the `rooms: [...]`
// payload.

import React from "react";
import SelectedRoomCard from "./SelectedRoomCard";
import { AMENITY_BY_KEY, parseAmenities } from "../utils/amenities";

export const BLANK_ROOM_LINE = {
  room_id: "",
  adults: 1, children: 0,
  rate_per_night: "", rate_per_hour: "", rate_per_day: "",
};

export default function MultiRoomEditor({
  rooms = [], roomTypes = [],
  value = [], billingMode = "nightly",
  onChange, money,
}) {
  const fmt = money || ((n) => `GH₵ ${Number(n || 0).toFixed(2)}`);

  const setLine = (idx, patch) => {
    const next = value.map((row, i) => i === idx ? { ...row, ...patch } : row);
    onChange(next);
  };

  const removeLine = (idx) => {
    if (value.length <= 1) return;
    onChange(value.filter((_, i) => i !== idx));
  };

  const addLine = () => {
    // Seed the new line's adults/children with sensible defaults and
    // let the operator pick the room.
    onChange([...value, { ...BLANK_ROOM_LINE }]);
  };

  // Default rate cascade from room + room type, used when the operator
  // hasn't typed an override on a line.
  const ratesForRoom = (roomId) => {
    const room = rooms.find(r => String(r.id) === String(roomId));
    if (!room) return { nightly: "", hourly: "", daily: "" };
    const type = roomTypes.find(t => t.id === room.room_type_id);
    return {
      nightly: room.price_per_night || type?.base_price || "",
      hourly:  type?.hourly_rate    || "",
      daily:   type?.daily_rate     || "",
    };
  };

  // Rate label shown next to each line based on the booking-level mode.
  const rateLabel = billingMode === "hourly" ? "Rate / hour"
                   : billingMode === "daily"  ? "Rate / day"
                                              : "Rate / night";
  const rateKey   = billingMode === "hourly" ? "rate_per_hour"
                   : billingMode === "daily"  ? "rate_per_day"
                                              : "rate_per_night";

  // IDs already chosen by other lines, so each select can grey out the
  // ones that are taken in this booking (the backend also rejects dupes).
  const takenIds = new Set(value.map(v => String(v.room_id)).filter(Boolean));

  return (
    <div>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        marginBottom: 8,
      }}>
        <div className="f-label" style={{ margin: 0 }}>
          Rooms in this booking ({value.length})
        </div>
        <button type="button" className="btn-outline"
          style={{ padding: "5px 12px", fontSize: 11 }}
          onClick={addLine}>
          + Add another room
        </button>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {value.map((line, idx) => {
          const selectedRoom = rooms.find(r => String(r.id) === String(line.room_id));
          return (
            <div key={idx} style={{
              border: "1px solid rgba(180,140,80,.22)",
              borderRadius: 9,
              background: idx % 2 === 0 ? "#fdf8f2" : "rgba(253,248,242,.55)",
              padding: "12px 14px",
            }}>
              <div style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                marginBottom: 10,
              }}>
                <div style={{
                  fontSize: 10.5, letterSpacing: ".14em", textTransform: "uppercase",
                  color: "#6b5142", fontWeight: 600,
                }}>
                  Room #{idx + 1}
                </div>
                {value.length > 1 && (
                  <button type="button"
                    onClick={() => removeLine(idx)}
                    title="Remove this room from the booking"
                    style={{
                      padding: "4px 10px", fontSize: 10.5,
                      background: "transparent",
                      border: "1.5px solid rgba(192,57,43,.3)",
                      borderRadius: 6, color: "#a93226",
                      cursor: "pointer", fontFamily: "'Jost', sans-serif",
                      letterSpacing: ".08em", textTransform: "uppercase",
                    }}>
                    ✕ Remove
                  </button>
                )}
              </div>

              <div className="form-grid">
                <div className="f-group" style={{ gridColumn: "1 / -1", marginBottom: 0 }}>
                  <label className="f-label">Room *</label>
                  <select
                    className="f-select"
                    value={line.room_id}
                    onChange={(e) => {
                      const id = e.target.value;
                      const { nightly, hourly, daily } = ratesForRoom(id);
                      setLine(idx, {
                        room_id: id,
                        rate_per_night: line.rate_per_night || nightly || "",
                        rate_per_hour:  line.rate_per_hour  || hourly  || "",
                        rate_per_day:   line.rate_per_day   || daily   || "",
                      });
                    }}
                    required
                  >
                    <option value="">Select room…</option>
                    {rooms.map(r => {
                      const amen = parseAmenities(r.amenities)
                        .slice(0, 4)
                        .map(k => AMENITY_BY_KEY[k]?.icon || "")
                        .filter(Boolean)
                        .join(" ");
                      const bits = [
                        `${r.room_number} — ${r.room_type_name || "Untyped"}`,
                        r.max_occupancy ? `${r.max_occupancy}pax` : null,
                        r.bed_type || null,
                        amen || null,
                        `${fmt(r.price_per_night)}/night`,
                        `[${r.status}]`,
                      ].filter(Boolean);
                      const isTakenElsewhere = takenIds.has(String(r.id)) && String(r.id) !== String(line.room_id);
                      return (
                        <option key={r.id} value={r.id} disabled={isTakenElsewhere}>
                          {bits.join(" · ")}{isTakenElsewhere ? "  — already on this booking" : ""}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div className="f-group" style={{ marginBottom: 0 }}>
                  <label className="f-label">Adults / Children</label>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input className="f-input" type="number" min="1"
                      value={line.adults}
                      onChange={(e) => setLine(idx, { adults: e.target.value })}/>
                    <input className="f-input" type="number" min="0"
                      value={line.children}
                      onChange={(e) => setLine(idx, { children: e.target.value })}/>
                  </div>
                </div>
                <div className="f-group" style={{ marginBottom: 0 }}>
                  <label className="f-label">{rateLabel} (GH₵)</label>
                  <input className="f-input" type="number" step="0.01"
                    placeholder="Defaults to type's rate"
                    value={line[rateKey] ?? ""}
                    onChange={(e) => setLine(idx, { [rateKey]: e.target.value })}/>
                </div>
              </div>

              {selectedRoom && (
                <div style={{ marginTop: 10 }}>
                  <SelectedRoomCard room={selectedRoom} money={fmt} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
